// [전체 코드 미사용/주석 삭제 금지]
import React, {useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {API_BASE_URL} from "config/apiConfig";

// CSS imports
import 'styles/Font.css';

// Image imports
import searchIcon from "assets/images/icon/search-icon.svg";
import hideIcon from "assets/images/icon/show-icon.svg";
import showIcon from "assets/images/icon/hide-icon.svg";
import remoteNum from "assets/images/icon/input-delete-icon.svg";

// Components imports
import useLockScroll from "hooks/useLockScroll";
import TopNavigator from "components/layout/TopNavigator/TopNavigator.jsx";
import PostcodeModal from "components/ui/Modals/PostcodeModal/PostcodeModal";
import usePreventInputScroll from "hooks/usePreventInputScroll";
import usePreventHorizontalScroll from "hooks/usePreventHorizontalScroll";
import ConfirmModal from "components/ui/Modals/ConfirmModal/ConfirmModal";

// 회원가입 페이지
function SignUp() {
    usePreventInputScroll();
    usePreventHorizontalScroll();

    const containerRef = useRef(null);
    const navigate = useNavigate();

    // 동적 높이 측정을 위한 ref 및 state
    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(0);

    // 입력창 포커스 이동을 위한 Refs 생성
    const passwordRef = useRef(null);
    const confirmPasswordRef = useRef(null);
    const userNameRef = useRef(null);
    const zipCodeRef = useRef(null);
    const addressRef = useRef(null);
    const detailAddressRef = useRef(null);

    // 상태
    const [userId, setUserId] = useState('');
    const [brKey, setBrKey] = useState('');
    const [brName, setBrName] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [formData, setFormData] = useState({
        password: '', confirmPassword: '', userName: '',
        zipCode: '', address: '', detailAddress: '', gender: '',
    });
    const [isPostcodeOpen, setIsPostcodeOpen] = useState(false);
    const [focusedInput, setFocusedInput] = useState(null);

    // API 통신 상태
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    // 알림 모달 상태
    const [alertModal, setAlertModal] = useState({
        isOpen: false,
        title: '',
        message: ''
    });

    // 뷰포트 높이 관리 State
    const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

    // 스크롤 잠금 훅
    useLockScroll(isPostcodeOpen);

    // 헤더 높이 측정 로직
    useEffect(() => {
        const measureHeight = () => {
            if (headerRef.current) {
                setHeaderHeight(headerRef.current.offsetHeight);
            }
        };

        // 초기 로드 시 측정
        measureHeight();

        // 레이아웃 시프트 대비 (폰트 로딩 등)
        setTimeout(measureHeight, 100);

        // 리사이즈 이벤트 감지
        window.addEventListener('resize', measureHeight);

        return () => window.removeEventListener('resize', measureHeight);
    }, []);

    // visualViewport 리사이즈 감지
    useEffect(() => {
        if (!isPostcodeOpen) return;

        const handleResize = () => {
            if (window.visualViewport) {
                setViewportHeight(window.visualViewport.height);
            }
        };

        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', handleResize);
            handleResize();
            return () => {
                window.visualViewport.removeEventListener('resize', handleResize);
            };
        }
    }, [isPostcodeOpen]);

    // 휴대폰 번호 받아오기
    useEffect(() => {
        const dataFromStorage = sessionStorage.getItem('tempSignUpData');
        const signUpData = dataFromStorage ? JSON.parse(dataFromStorage) : {};
        if (signUpData.phoneNumber) {
            setUserId(signUpData.phoneNumber);
        }
        // 지점 선택 스텝(/signup/branch)에서 저장한 지점 정보 복원
        if (signUpData.brKey) {
            setBrKey(signUpData.brKey);
            setBrName(signUpData.brName || '');
        } else {
            // 지점 미선택 상태로 이 화면에 진입한 경우 (뒤로가기 등) 선택 화면으로 되돌림
            navigate('/signup/branch', { replace: true });
        }
    }, [navigate]);

    // 핸들러
    const handleChange = (e) => {
        const {name, value} = e.target;
        setFormData(prevData => ({...prevData, [name]: value}));
    };
    const handleGenderClick = (selectedGender) => {
        setFormData(prevData => ({...prevData, gender: selectedGender}));
    };

    // [수정됨] 비밀번호 유효성 검사 로직 (10자 이상 단독 허용 로직 제거)
    const validatePassword = (pwd) => {
        if (!pwd) return false;

        // 8자리 이상이면서 2종류 이상 조합 필수
        if (pwd.length >= 8) {
            const hasLetter = /[a-zA-Z]/.test(pwd); // 영문
            const hasNum = /[0-9]/.test(pwd); // 숫자
            const hasSpecial = /[^a-zA-Z0-9]/.test(pwd); // 특수문자

            const typeCount = [hasLetter, hasNum, hasSpecial].filter(Boolean).length;
            return typeCount >= 2;
        }

        return false;
    };

    // 파생 상태
    const isPasswordMatching = formData.password && formData.password === formData.confirmPassword;
    const isPasswordValid = validatePassword(formData.password);

    const isButtonActive =
        isPasswordValid &&
        isPasswordMatching &&
        formData.userName.trim() !== '' &&
        formData.gender &&
        !!brKey;
    // formData.zipCode.trim() !== '' &&
    // formData.address.trim() !== '';

    // 모달 닫기 핸들러
    const closeAlertModal = () => {
        setAlertModal(prev => ({...prev, isOpen: false}));
    };

    const handleSignUpClick = async () => {
        const SIGNUP_API_URL = `${API_BASE_URL}/signup`;

        if (!isButtonActive || isLoading) return;

        setIsLoading(true);
        setErrorMessage(null);

        const storedData = sessionStorage.getItem('tempSignUpData');
        const parsedData = storedData ? JSON.parse(storedData) : {};
        const marketingValue = parsedData.agreementMarketing ? 'Y' : 'N';

        const payload = {
            phoneNumber: userId,
            password: formData.password,
            passwordCheck: formData.confirmPassword,
            userName: formData.userName,
            gender: formData.gender,
            postCode: formData.zipCode,
            address: formData.address,
            detailAddress: formData.detailAddress,
            agreementMarketing: marketingValue,
            brKey: brKey
        };

        try {
            const response = await fetch(SIGNUP_API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams(payload)
            });

            const res = await response.json();
            const data = res.data;

            // code가 200이 아니면 무조건 에러 처리
            if (data.code !== 200) {
                throw new Error(data.message || '회원가입에 실패했습니다.');
            }

            sessionStorage.removeItem('tempSignUpData');

            setAlertModal({
                isOpen: true,
                title: '가입 완료',
                message: '회원가입이 완료되었습니다.\n로그인 화면으로 이동합니다.',
                onConfirm: () => navigate('/', {replace: true})
            });

        } catch (error) {
            console.error('SignUp Error:', error);
            const errorMessageText = error.message || '회원가입 중 오류가 발생했습니다.';

            setAlertModal({
                isOpen: true,
                title: '가입 실패',
                message: errorMessageText
            });
            setErrorMessage(errorMessageText);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e, nextRef) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (nextRef && nextRef.current) {
                nextRef.current.focus();
            } else {
                e.target.blur();
            }
        }
    };

    // 우편번호 검색 관련 핸들러
    const handleOpenPostcode = () => {
        setIsPostcodeOpen(true);
    };

    const handleClosePostcode = () => {
        setIsPostcodeOpen(false);
    };

    const handlePostcodeComplete = (data) => {
        let addr = '';
        if (data.userSelectedType === 'R') {
            addr = data.roadAddress;
        } else {
            addr = data.jibunAddress;
        }

        setFormData(prevData => ({
            ...prevData,
            zipCode: data.zonecode,
            address: addr,
        }));

        if (detailAddressRef.current) {
            detailAddressRef.current.focus();
        }
    };

    // 동적 스타일 적용 (paddingTop 계산)
    const dynamicMainStyle = {
        ...staticStyles.mainContent,
        paddingTop: `${headerHeight}px`,
    };

    return (
        <div className="container signup-page-container" ref={containerRef} style={staticStyles.backgroundWrapper}>

            {/* ref 연결 */}
            <div style={staticStyles.fixedHeaderWrapper} ref={headerRef}>
                <div className="statusBar" style={staticStyles.statusBar}/>
                <TopNavigator title="회원가입"/>
            </div>

            {/* 동적 스타일 적용 */}
            <main className="content-wrapper signup-content" style={dynamicMainStyle}>
                {/* 아이디 (자동표시) */}
                <div className="id-input-area" style={staticStyles.idInputArea}>
                    <div className="auto-filled">
                        <label className="signup-id-label" style={staticStyles.signupLabel}>아이디</label>
                        <input
                            className="common-input"
                            style={staticStyles.commonInput}
                            type="text"
                            value={userId}
                            readOnly
                        />
                    </div>
                    <div className="auto-filled-info" style={staticStyles.autoFilledInfo}>
                        <span className="signup-info-text" style={staticStyles.signupInfoText}>
                            인증된 휴대폰번호가 아이디로 자동 등록됩니다.
                        </span>
                    </div>
                </div>

                {/* 선택한 지점 (자동표시) */}
                <div className="branch-input-area" style={staticStyles.inputArea}>
                    <label className="signup-branch-label" style={staticStyles.signupLabel}>이용 지점</label>
                    <input
                        className="common-input"
                        style={staticStyles.commonInput}
                        type="text"
                        value={brName}
                        readOnly
                    />
                </div>

                {/* 비밀번호 */}
                <div className="pw-input-area" style={staticStyles.inputArea}>
                    <label className="signup-pw-label" style={staticStyles.signupLabel}>비밀번호</label>
                    <div className="input-with-icon" style={staticStyles.inputWithIcon}>
                        <input
                            ref={passwordRef}
                            onKeyDown={(e) => handleKeyDown(e, confirmPasswordRef)}
                            type={showPassword ? "text" : "password"}
                            className="common-input"
                            style={{
                                ...staticStyles.commonInput,
                                ...(focusedInput === 'pw' ? staticStyles.inputFocused : {})
                            }}
                            placeholder="비밀번호 입력"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            onFocus={() => setFocusedInput('pw')}
                            onBlur={() => setFocusedInput(null)}
                        />
                        <img
                            src={showPassword ? showIcon : hideIcon}
                            alt="비밀번호 보기/숨기기"
                            style={staticStyles.inputIcon}
                            onClick={() => setShowPassword(!showPassword)}
                        />
                    </div>

                    {/* [수정됨] 비밀번호 유효성 검사 메시지 변경 */}
                    {formData.password.length > 0 && !isPasswordValid && (
                        <p style={{
                            ...staticStyles.helperText,
                            ...staticStyles.helperTextError,
                            marginBottom: '10px'
                        }}>
                            비밀번호는 8자 이상, 2종류 이상(영문/숫자/특수문자)을 조합해야 합니다.
                        </p>
                    )}

                    <div className="input-with-icon" style={staticStyles.inputWithIcon}>
                        <input
                            ref={confirmPasswordRef}
                            onKeyDown={(e) => handleKeyDown(e, userNameRef)}
                            type={showConfirmPassword ? "text" : "password"}
                            className="common-input"
                            style={{
                                ...staticStyles.commonInput,
                                ...(focusedInput === 'confirmPw' ? staticStyles.inputFocused : {})
                            }}
                            placeholder="비밀번호 확인"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            onFocus={() => setFocusedInput('confirmPw')}
                            onBlur={() => setFocusedInput(null)}
                        />
                        <img
                            src={showConfirmPassword ? showIcon : hideIcon}
                            alt="비밀번호 확인 보기/숨기기"
                            style={staticStyles.inputIcon}
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        />
                    </div>

                    {formData.confirmPassword.length > 0 && (
                        <p style={{
                            ...staticStyles.helperText,
                            ...(isPasswordMatching ? staticStyles.helperTextSuccess : staticStyles.helperTextError)
                        }}>
                            {isPasswordMatching
                                ? '비밀번호가 일치합니다'
                                : '비밀번호가 일치하지 않습니다'
                            }
                        </p>
                    )}
                </div>

                {/* 이름 */}
                <div className="name-input-area" style={staticStyles.inputArea}>
                    <label className="signup-name-label" style={staticStyles.signupLabel}>이름</label>
                    <input
                        ref={userNameRef}
                        onKeyDown={(e) => handleKeyDown(e, zipCodeRef)}
                        className="common-input"
                        style={{
                            ...staticStyles.commonInput,
                            ...(focusedInput === 'name' ? staticStyles.inputFocused : {})
                        }}
                        placeholder="이름 입력"
                        name="userName"
                        value={formData.userName}
                        onChange={handleChange}
                        lang="ko"
                        inputMode="text"
                        onFocus={() => setFocusedInput('name')}
                        onBlur={() => setFocusedInput(null)}
                    />
                </div>

                {/* 성별 */}
                <div className="gender-select-area" style={staticStyles.inputArea}>
                    <label className="signup-gender-label" style={staticStyles.signupLabel}>성별</label>
                    <div className="gender-buttons" style={staticStyles.genderButtons}>
                        <button
                            style={{
                                ...staticStyles.genderButton,
                                ...(formData.gender === 'female' ? staticStyles.genderButtonActive : {})
                            }}
                            onClick={() => handleGenderClick('female')}
                        >여성
                        </button>
                        <button
                            style={{
                                ...staticStyles.genderButton,
                                ...(formData.gender === 'male' ? staticStyles.genderButtonActive : {})
                            }}
                            onClick={() => handleGenderClick('male')}
                        >남성
                        </button>
                    </div>
                </div>

                {/* 주소 */}
                <div className="address-input-area" style={staticStyles.inputArea}>
                    <label className="signup-address-label" style={staticStyles.signupLabel}>주소</label>

                    <div className="input-with-icon" style={{...staticStyles.inputWithIcon, marginBottom: 0}}>
                        <input
                            ref={zipCodeRef}
                            onKeyDown={(e) => handleKeyDown(e, addressRef)}
                            className="common-input"
                            style={{
                                ...staticStyles.commonInput,
                                ...(focusedInput === 'zipCode' ? staticStyles.inputFocused : {}),
                                marginBottom: 'clamp(10px, 2.67vw, 12px)'
                            }}
                            placeholder="우편번호"
                            name="zipCode"
                            value={formData.zipCode}
                            onChange={handleChange}
                            onFocus={() => setFocusedInput('zipCode')}
                            onBlur={() => setFocusedInput(null)}
                        />
                        <img
                            src={searchIcon}
                            alt="우편번호 검색"
                            style={{...staticStyles.inputIcon, cursor: 'pointer'}}
                            onClick={handleOpenPostcode}
                        />
                    </div>

                    <div className="input-with-icon" style={staticStyles.inputWithIcon}>
                        <input
                            ref={addressRef}
                            onKeyDown={(e) => handleKeyDown(e, detailAddressRef)}
                            className="common-input"
                            style={{
                                ...staticStyles.commonInput,
                                ...(focusedInput === 'address' ? staticStyles.inputFocused : {})
                            }}
                            placeholder="도로명, 지번, 건물명 검색"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            onFocus={() => setFocusedInput('address')}
                            onBlur={() => setFocusedInput(null)}
                        />
                    </div>

                    <div className="input-with-icon" style={{...staticStyles.inputWithIcon, marginBottom: 0}}>
                        <input
                            ref={detailAddressRef}
                            onKeyDown={(e) => handleKeyDown(e, null)}
                            className="common-input"
                            style={{
                                ...staticStyles.commonInput,
                                ...(focusedInput === 'detailAddress' ? staticStyles.inputFocused : {}),
                            }}
                            placeholder="상세주소 입력"
                            name="detailAddress"
                            value={formData.detailAddress}
                            onChange={handleChange}
                            onFocus={() => setFocusedInput('detailAddress')}
                            onBlur={() => setFocusedInput(null)}
                        />
                        {formData.detailAddress.length > 0 && (
                            <img
                                src={remoteNum}
                                alt="입력 내용 지우기"
                                style={staticStyles.inputIcon}
                                onClick={() => setFormData(prev => ({...prev, detailAddress: ''}))}
                            />
                        )}
                    </div>
                </div>

                {errorMessage && (
                    <div style={{color: 'red', textAlign: 'center', marginTop: '10px'}}>
                        {errorMessage}
                    </div>
                )}

                <button
                    style={{
                        ...staticStyles.signupPageButton,
                        ...(isButtonActive && !isLoading ? staticStyles.signupPageButtonActive : {}),
                        ...(isLoading ? {background: '#5a5a6a', cursor: 'wait'} : {})
                    }}
                    disabled={!isButtonActive || isLoading}
                    onClick={handleSignUpClick}
                >
                    <p style={staticStyles.signupPageButtonLabel}>
                        {isLoading ? '가입 처리 중...' : '가입하기'}
                    </p>
                </button>
            </main>

            <PostcodeModal
                isOpen={isPostcodeOpen}
                onClose={handleClosePostcode}
                onComplete={handlePostcodeComplete}
                viewportHeight={viewportHeight}
            />

            <ConfirmModal
                isOpen={alertModal.isOpen}
                onClose={closeAlertModal}
                onConfirm={() => {
                    closeAlertModal();
                    if (alertModal.onConfirm) alertModal.onConfirm();
                }}
                title={alertModal.title}
                message={alertModal.message}
                showCancel={false}
            />
        </div>
    );
}

/** @type {React.CSSProperties} */
const staticStyles = {
    // --- 1. 레이아웃 ---
    backgroundWrapper: {
        background: '#0D042D',
        width: '100%',
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        margin: '0 auto',
        position: 'relative',
        overflow: 'hidden',
    },
    fixedHeaderWrapper: {
        position: 'fixed',
        top: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: '430px',
        zIndex: 100,
        backgroundColor: '#0D042D',
    },
    statusBar: {
        flexShrink: 0,
        backgroundColor: 'transparent',
    },
    // --- 2. 스크롤되는 메인 콘텐츠 ---
    mainContent: {
        flex: 1,
        overflowY: 'auto',
        width: '100%',
        maxWidth: '430px',
        margin: '0 auto',
        // 상단 패딩은 동적으로 계산되므로 0 또는 초기값으로 설정
        paddingTop: '0',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 24px) + 24px)',
        paddingLeft: '24px',
        paddingRight: '24px',
        boxSizing: 'border-box',
    },
    // --- 3. 폼 요소 ---
    idInputArea: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        marginTop: '25px',
    },
    inputArea: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        marginTop: 'clamp(32px, 8.53vw, 37px)',
    },
    signupLabel: {
        fontFamily: '"Pretendard", sans-serif',
        fontWeight: 500,
        fontSize: 'clamp(14px, 3.73vw, 16px)',
        color: '#9A9A9A',
        marginBottom: '8px',
    },
    commonInput: {
        height: 'clamp(50px, 13.33vw, 58px)',
        padding: 'clamp(16px, 4.27vw, 18px) clamp(14px, 3.73vw, 16px)',
        fontSize: 'clamp(16px, 4.27vw, 18px)',
        borderRadius: 'clamp(6px, 1.6vw, 7px)',
        border: '1px solid transparent',
        width: '100%',
        boxSizing: 'border-box',
        background: '#1B1931',
        color: '#FCFCFA',
        fontFamily: '"Pretendard", sans-serif',
        fontWeight: '500',
        outline: 'none',
        '::placeholder': {
            fontFamily: '"Pretendard", sans-serif',
            fontWeight: '500',
            fontSize: 'clamp(16px, 4.27vw, 18px)',
            color: '#747474',
        },
    },
    inputFocused: {
        background: 'linear-gradient(#1B1931, #1B1931), linear-gradient(90deg, #8052FF 0%, #FFFFFF 50%, #0231FB 100%)',
        backgroundOrigin: 'border-box',
        backgroundClip: 'padding-box, border-box',
    },
    autoFilledInfo: {
        marginTop: 'clamp(8px, 2.13vw, 9px)',
    },
    signupInfoText: {
        fontFamily: '"Pretendard", sans-serif',
        fontWeight: 500,
        fontSize: 'clamp(12px, 3.2vw, 14px)',
        color: '#9A9A9A',
    },
    helperText: {
        fontFamily: '"Pretendard", sans-serif',
        fontWeight: 500,
        fontSize: 'clamp(12px, 3.2vw, 14px)',
        margin: '4px 0 0 0',
        lineHeight: 1.4,
    },
    helperTextError: {
        color: '#FF4D4D',
    },
    helperTextSuccess: {
        color: '#4CAF50',
    },
    inputWithIcon: {
        position: 'relative',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        marginBottom: 'clamp(10px, 2.67vw, 12px)',
    },
    inputIcon: {
        position: 'absolute',
        right: 'clamp(14px, 3.73vw, 16px)',
        width: 'clamp(24px, 6.4vw, 28px)',
        height: 'clamp(24px, 6.4vw, 28px)',
        cursor: 'pointer',
    },
    genderButtons: {
        display: 'flex',
        width: '100%',
        gap: 'clamp(10px, 2.67vw, 12px)',
    },
    genderButton: {
        flex: 1,
        height: 'clamp(50px, 13.33vw, 58px)',
        borderRadius: 'clamp(6px, 1.6vw, 7px)',
        background: '#1B1931',
        border: '1px solid #1B1931',
        color: '#9A9A9A',
        fontFamily: '"Pretendard", sans-serif',
        fontWeight: 600,
        fontSize: 'clamp(16px, 4.27vw, 18px)',
        cursor: 'pointer',
    },
    genderButtonActive: {
        background: 'linear-gradient(90deg, #8052FF 0%, #0231FB 100%)',
        color: '#F5F8F9',
    },
    signupPageButton: {
        width: '100%',
        height: 'clamp(56px, 14.93vw, 64px)',
        border: 'none',
        borderRadius: 'clamp(6px, 1.6vw, 7px)',
        background: '#3A3A4A',
        cursor: 'not-allowed',
        margin: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: 'clamp(32px, 8.53vw, 37px)',
    },
    signupPageButtonActive: {
        background: 'linear-gradient(90deg, #8052FF 0%, #0231FB 100%)',
        cursor: 'pointer',
    },
    signupPageButtonLabel: {
        fontFamily: '"Pretendard", sans-serif',
        fontWeight: 700,
        fontSize: 'clamp(16px, 4.27vw, 18px)',
        color: '#FFFFFF',
        margin: 0,
    },
};

export default SignUp;